import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CustomRSVPBuilder, CustomRSVPQuestion } from './CustomRSVPBuilder';
import { RSVPAnalyticsDashboard } from './RSVPAnalyticsDashboard';
import { useCustomRSVP } from '@/hooks/useCustomRSVP';
import { Settings, BarChart3, FileText, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

interface CustomRSVPManagerProps {
  eventId: string;
  eventName: string;
}

export function CustomRSVPManager({ eventId, eventName }: CustomRSVPManagerProps) {
  const isMobile = useIsMobile();
  const { 
    rsvpForm, 
    responses, 
    loading, 
    error, 
    saveRSVPForm,
    getResponseAnalytics 
  } = useCustomRSVP(eventId);

  const [activeTab, setActiveTab] = useState('builder');
  const [questions, setQuestions] = useState<CustomRSVPQuestion[]>(
    rsvpForm?.questions || []
  );

  // Update questions when rsvpForm changes
  useEffect(() => {
    if (rsvpForm?.questions) {
      setQuestions(rsvpForm.questions);
    }
  }, [rsvpForm]);

  const handleSaveForm = async () => {
    try {
      await saveRSVPForm(questions);
      toast.success('RSVP form saved successfully');
    } catch (error) {
      toast.error('Failed to save RSVP form');
    }
  };

  const analytics = getResponseAnalytics();
  const responseCount = analytics ? analytics.totalResponses : 0;
  const questionCount = questions.length;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p>Loading RSVP form...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-destructive">Error: {error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Custom RSVP Forms</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {isMobile ? eventName : `Advanced RSVP management for ${eventName}`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4">
          <div className="text-xs md:text-sm text-muted-foreground">
            <span className="font-medium">{questionCount}</span> questions • 
            <span className="font-medium ml-1">{responseCount}</span> responses
          </div>
          {activeTab === 'builder' && (
            <Button 
              onClick={handleSaveForm} 
              disabled={questions.length === 0}
              className="w-full sm:w-auto"
            >
              <Settings className="h-4 w-4 mr-2" />
              Save Form
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
          <TabsTrigger value="builder" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
            <FileText className="h-3 w-3 md:h-4 md:w-4" />
            {isMobile ? 'Builder' : 'Form Builder'}
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
            <BarChart3 className="h-3 w-3 md:h-4 md:w-4" />
            Analytics
          </TabsTrigger>
          {!isMobile && (
            <>
              <TabsTrigger value="responses" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Responses
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="builder" className="space-y-6">
          <CustomRSVPBuilder
            questions={questions}
            onQuestionsChange={setQuestions}
            eventId={eventId}
          />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <RSVPAnalyticsDashboard eventId={eventId} />
        </TabsContent>

        {(!isMobile || activeTab === 'responses') && (
          <TabsContent value="responses" className="space-y-4 md:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">RSVP Responses</CardTitle>
              </CardHeader>
              <CardContent>
                {responseCount === 0 ? (
                  <p className="text-muted-foreground text-center py-8 text-sm md:text-base">
                    No responses yet. Share your event to start collecting RSVPs.
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                      <Card>
                        <CardContent className="pt-4 md:pt-6">
                          <div className="text-xl md:text-2xl font-bold">{responseCount}</div>
                          <p className="text-xs text-muted-foreground">Total Responses</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 md:pt-6">
                          <div className="text-xl md:text-2xl font-bold">{questionCount}</div>
                          <p className="text-xs text-muted-foreground">Questions</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 md:pt-6">
                          <div className="text-xl md:text-2xl font-bold">
                            {questionCount > 0 ? ((responseCount / questionCount) * 100).toFixed(1) : 0}%
                          </div>
                          <p className="text-xs text-muted-foreground">Completion Rate</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {(!isMobile || activeTab === 'settings') && (
          <TabsContent value="settings" className="space-y-4 md:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg md:text-xl">Form Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4">
                <div className="space-y-1 md:space-y-2">
                  <h4 className="font-medium text-sm md:text-base">Form Status</h4>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Form is currently {rsvpForm?.is_active ? 'active' : 'inactive'}
                  </p>
                </div>
                
                <div className="space-y-1 md:space-y-2">
                  <h4 className="font-medium text-sm md:text-base">Last Updated</h4>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {rsvpForm?.updated_at ? new Date(rsvpForm.updated_at).toLocaleString() : 'Never'}
                  </p>
                </div>

                <div className="space-y-1 md:space-y-2">
                  <h4 className="font-medium text-sm md:text-base">Questions</h4>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {questionCount} custom questions configured
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}